import React, { useEffect } from 'react';
import { Routes, Route, useNavigate, Navigate, useLocation } from 'react-router-dom';
import { ErrorBoundary } from '@ohif/ui-next';

// Route Components
import DataSourceWrapper from './DataSourceWrapper';
import WorkList from './WorkList';
import Local from './Local';
import Debug from './Debug';
import NotFound from './NotFound';
import buildModeRoutes from './buildModeRoutes';
import PrivateRoute from './PrivateRoute';
import TokenRefreshManager from '../../../custom/components/TokenRefreshManager';
import ClinicFeatureSync from '../../../custom/components/ClinicFeatureSync';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

// Custom Routes
import {
  myProfile,
  auditLogs,
  Settings,
  PatientList,
  Dashboard,
  ChangePassword,
  UserList,
  AddUpdateUser,
  ClinicsList,
  Billing,
  FeatureControl,
  ReportList,
  PatientReport,
  AddUpdateClinic,
  Login,
  ForgotPasswordV2,
  OtpVerification,
  ResetPassword,
  ClinicProfile,
  ReportTemplate,
  TemplateListV2,
  UploadTemplate,
  Pacs,
  AddUpdatePacs,
  Preview,
  ExpressMode,
  DistributionPage,
  SharedClipsViewer,
  GpLoginV2,
  GpReportsList,
  GpReportView,
  GpAccountsAdmin,
  ClinicGroups,
  GroupAdminStudies,
  GroupAdminReports,
  GroupAdminReportView,
  MobileReportEditor,
  DropdownSettingsPage,
} from '../../../custom/pages';

import { isValidUser, getRole } from '../../../custom/Utils/authUtils';
import {
  isDicomViewerEnabled,
  isMeasurementEnabled,
} from '../../../custom/Utils/clinicFeatures';
import { isValidGp } from '../../../custom/Utils/gpAuth';

// ── Role-based access control ────────────────────────────────────────────────
// Shorthand groups to keep the route table readable.
const SUPER = ['SUPER_ADMIN', 'System Admin'];
const ADMIN_ONLY = [...SUPER, 'Admin'];
const CLINICAL = ['Admin', 'Sonographer', 'Cardiologist', 'Radiologist', 'Clinician', 'Practice Manager', 'Medical Receptionist'];
const CLINICAL_NO_RECEP = ['Admin', 'Sonographer', 'Cardiologist', 'Radiologist', 'Clinician', 'Practice Manager'];
const GROUP_ADMIN = ['Group Admin'];
const ALL_ROLES = [...SUPER, ...CLINICAL, ...GROUP_ADMIN];
const ALL_EXCEPT_RECEP = [...SUPER, ...CLINICAL_NO_RECEP];
const ADMIN_SONO = ['Admin', 'Sonographer'];
const ADMIN_CLINIC = ['Admin'];
// Report Dropdowns editors: clinic Admin + sonographers + reading doctors (there is no
// literal 'Doctor' role — reading doctors are Cardiologist/Radiologist/Clinician).
const DROPDOWN_ROLES = ['Admin', 'Sonographer', 'Cardiologist', 'Radiologist', 'Clinician'];

// ── Per-clinic feature control ───────────────────────────────────────────────
// A route may additionally require a feature the clinic has been given (Super
// Admin → Feature control). Roles say WHO may open a screen; this says whether
// the clinic bought it at all.
type ClinicFeatureName = 'dicomViewer' | 'measurement';

const isClinicFeatureAllowed = (feature: ClinicFeatureName): boolean =>
  feature === 'measurement' ? isMeasurementEnabled() : isDicomViewerEnabled();

/**
 * Which feature a mode route belongs to, by its routeName.
 *
 * `viewer` is the cardiac-echo/longitudinal OHIF viewer; `measurement` is the
 * measurement workspace. Every other mode route (dev, basic, microscopy…) is
 * ungated — they are OHIF's own and not sold per clinic.
 */
const MODE_ROUTE_FEATURES: Record<string, ClinicFeatureName> = {
  '/viewer': 'dicomViewer',
  '/measurement': 'measurement',
};

/** '/viewer/dicomweb' → '/viewer'. Mode routes exist per data source too. */
const featureForModeRoute = (path: string): ClinicFeatureName | undefined =>
  MODE_ROUTE_FEATURES[`/${String(path || '').split('/').filter(Boolean)[0] || ''}`];

/**
 * Returns the default landing page for a given role.
 * Used as the redirect target when a user tries to access a route they don't
 * have permission for.
 */
const getDefaultRouteForRole = (role: string): string => {
  if (role === 'SUPER_ADMIN' || role === 'System Admin') return '/clinics';
  if (role === 'Sonographer') return '/';
  if (role === 'Admin' || role === 'Group Admin') return '/dashboard';
  // Cardiologist, Radiologist, Clinician, Practice Manager, Medical Receptionist
  return '/reports';
};

const NotFoundServer = ({
  message = 'Unable to query for studies at this time. Check your data source configuration or network connection',
}) => {
  return (
    <div className="absolute flex h-full w-full items-center justify-center text-white">
      <div>
        <h4>{message}</h4>
      </div>
    </div>
  );
};

NotFoundServer.propTypes = {
  message: PropTypes.string,
};

const NotFoundStudy = () => {
  return (
    <div className="absolute flex h-full w-full items-center justify-center text-white">
      <div>
        <h4>
          One or more of the requested studies are not available at this time. Return to the{' '}
          <Link
            className="text-primary-light"
            to={'/'}
          >
            study list
          </Link>{' '}
          to select a different study to view.
        </h4>
      </div>
    </div>
  );
};

NotFoundStudy.propTypes = {
  message: PropTypes.string,
};

// TODO: Include "routes" debug route if dev build
const bakedInRoutes = [
  // ── Error / utility routes (no role restriction) ───────────────────────────
  {
    path: '/notfoundserver',
    children: NotFoundServer,
  },
  {
    path: '/notfoundstudy',
    children: NotFoundStudy,
  },
  {
    path: '/debug',
    children: Debug,
  },
  {
    path: '/local',
    children: Local.bind(null, { modePath: '' }),
  },
  {
    path: '/localbasic',
    children: Local.bind(null, { modePath: 'viewer/dicomlocal' }),
  },

  // ── Common routes (all authenticated roles) ────────────────────────────────
  { path: '/dashboard',       children: Dashboard,       allowedRoles: ALL_ROLES },
  { path: '/myProfile',       children: myProfile,       allowedRoles: ALL_ROLES },
  { path: '/changePassword',  children: ChangePassword,  allowedRoles: ALL_ROLES },
  { path: '/settings',        children: Settings,        allowedRoles: ALL_EXCEPT_RECEP },

  // ── Clinic management (SUPER_ADMIN only) ───────────────────────────────────
  { path: '/clinics',            children: ClinicsList,      allowedRoles: SUPER },
  { path: '/addclinic',          children: AddUpdateClinic,  allowedRoles: SUPER },
  { path: '/updateclinic/:id',   children: AddUpdateClinic,  allowedRoles: SUPER },

  // ── Billing / money tracking (SUPER_ADMIN only) ────────────────────────────
  { path: '/billing',            children: Billing,          allowedRoles: SUPER },

  // ── Per-clinic feature control (SUPER_ADMIN only) ──────────────────────────
  { path: '/featureControl',      children: FeatureControl,   allowedRoles: SUPER },
  { path: '/clinicGroups',        children: ClinicGroups,     allowedRoles: SUPER },

  // ── User management (SUPER_ADMIN + Admin) ──────────────────────────────────
  { path: '/users',              children: UserList,         allowedRoles: ADMIN_ONLY },
  { path: '/adduser',            children: AddUpdateUser,    allowedRoles: ADMIN_ONLY },
  { path: '/updateuser/:id',     children: AddUpdateUser,    allowedRoles: ADMIN_ONLY },
  { path: '/gpAccounts',         children: GpAccountsAdmin,  allowedRoles: ADMIN_ONLY },

  // ── Audit logs (Admin + SUPER_ADMIN — matches the only nav entry, Header.tsx) ──
  { path: '/auditLogs',          children: auditLogs,        allowedRoles: ADMIN_ONLY },

  // ── PACS management ───────────────────────────────────────────────────────
  { path: '/pacs',               children: Pacs,             allowedRoles: ADMIN_SONO },
  { path: '/addpacs',            children: AddUpdatePacs,    allowedRoles: ADMIN_CLINIC },

  // ── Clinic-level admin (Admin only) ───────────────────────────────────────
  { path: '/clinicProfile',      children: ClinicProfile,    allowedRoles: ADMIN_CLINIC },
  { path: '/reportTemplate',     children: ReportTemplate,   allowedRoles: ADMIN_CLINIC },
  { path: '/dropdownSettings',   children: DropdownSettingsPage, allowedRoles: DROPDOWN_ROLES },

  // ── Clinical routes (all non-SUPER roles) ──────────────────────────────────
  { path: '/reports',                       children: ReportList,        allowedRoles: CLINICAL },
  { path: '/patientReports/:_id',           children: PatientReport,     allowedRoles: CLINICAL },
  { path: '/templates',                     children: TemplateListV2,    allowedRoles: CLINICAL },
  { path: '/uploadtemplate',                children: UploadTemplate,    allowedRoles: CLINICAL },
  { path: '/templates/preview/:id',         children: Preview,           allowedRoles: CLINICAL },
  { path: '/patients',                      children: PatientList,       allowedRoles: CLINICAL },
  { path: '/express/:studyInstanceUIDs',    children: ExpressMode,       allowedRoles: CLINICAL_NO_RECEP },
  { path: '/distribution/:studyInstanceUID', children: DistributionPage, allowedRoles: CLINICAL },

  // ── Group Admin (read-only over its clinic group; dashboard is /dashboard) ──
  { path: '/group-admin/studies',           children: GroupAdminStudies,    allowedRoles: GROUP_ADMIN },
  { path: '/group-admin/reports',           children: GroupAdminReports,    allowedRoles: GROUP_ADMIN },
  { path: '/group-admin/reports/:reportId', children: GroupAdminReportView, allowedRoles: GROUP_ADMIN },
];

// ── GP portal (fully isolated world: own token, own guard, own components) ──
// GP routes never use ProtectedRoute/isValidUser — they check the GP token only.
const GpProtectedRoute = ({ element }: { element: React.ReactElement }) => {
  if (!isValidGp()) {
    return <Navigate to="/gp/login" replace />;
  }
  return element;
};

const gpRoutes = [
  { path: '/gp/reports', children: GpReportsList },
  { path: '/gp/reports/:reportId', children: GpReportView },
];

// ── Mobile WebView report editor ─────────────────────────────────────────────
// No ProtectedRoute — the page itself handles auth via postMessage from React Native.
const mobileRoutes = [
  { path: '/mobile/report/:id', children: MobileReportEditor },
];

const unauthorizedRoutes = [
  {
    path: '/login',
    children: Login,
  },
  {
    path: '/gp/login',
    children: GpLoginV2,
  },
  {
    path: '/forgotPassword',
    children: ForgotPasswordV2,
  },
  {
    path: '/resetPassword',
    children: ResetPassword,
  },
  {
    path: '/otpVerification',
    children: OtpVerification,
  },
  {
    path: '/shared/:uuid',
    children: SharedClipsViewer,
  },
];

// NOT FOUND (404) – redirect to role-based default page
const RoleBasedCatchAll = () => {
  if (!isValidUser()) {
    // GP session active → GP world; otherwise normal login
    if (isValidGp()) {
      return <Navigate to="/gp/reports" replace />;
    }
    return <Navigate to="/login" replace />;
  }
  const role = getRole() || '';
  return <Navigate to={getDefaultRouteForRole(role)} replace />;
};
const notFoundRoute = { path: '*', children: RoleBasedCatchAll };

/**
 * Checks authentication AND role-based access.
 *
 * - Not authenticated → redirect to /login
 * - Authenticated but role not in allowedRoles → redirect to the role's
 *   default landing page (e.g. /reports for Cardiologists)
 * - If no allowedRoles are specified the route is open to any authenticated user
 */
const ProtectedRoute = ({
  element,
  allowedRoles,
  requiredFeature,
}: {
  element: React.ReactElement;
  allowedRoles?: string[];
  requiredFeature?: ClinicFeatureName;
}) => {
  if (!isValidUser()) {
    return <Navigate to="/login" />;
  }

  if (allowedRoles && allowedRoles.length > 0) {
    const role = getRole();
    if (!role || !allowedRoles.includes(role)) {
      return <Navigate to={getDefaultRouteForRole(role || '')} replace />;
    }
  }

  // Per-clinic feature control. Hiding the entry button is not access control —
  // /viewer and /measurement are URLs anyone can type or bookmark. The flags are
  // read from the storage cache (ClinicFeatureSync keeps it fresh) so this stays
  // synchronous; the server refuses the measurement endpoints independently.
  if (requiredFeature && !isClinicFeatureAllowed(requiredFeature)) {
    const role = getRole();
    return <Navigate to={getDefaultRouteForRole(role || '')} replace />;
  }

  return element;
};

const createRoutes = ({
  modes,
  dataSources,
  extensionManager,
  servicesManager,
  commandsManager,
  hotkeysManager,
  routerBasename,
  showStudyList,
}: withAppTypes) => {
  const routes =
    buildModeRoutes({
      modes,
      dataSources,
      extensionManager,
      servicesManager,
      commandsManager,
      hotkeysManager,
    }) || [];

  const { customizationService } = servicesManager.services;

  const WorkListRoute = {
    path: '/',
    children: DataSourceWrapper,
    private: true,
    allowedRoles: CLINICAL_NO_RECEP,
    props: { children: WorkList, servicesManager, extensionManager },
  };

  // 3.12 removed getModeCustomization/getGlobalCustomization in favour of a single
  // getCustomization, and renamed the id from 'customRoutes' to 'routes.customRoutes'.
  // Both changes are needed — the method alone would return undefined.
  const customRoutes = customizationService.getCustomization('routes.customRoutes');
  // Tag the OHIF mode routes with the clinic feature they belong to, so a clinic
  // without the viewer (or measurement) cannot reach it by URL. Derived from the
  // route path rather than hardcoded per mode, so an added data-source variant
  // (/viewer/dicomweb) is covered automatically.
  const modeRoutes = routes.map(route => {
    const requiredFeature = featureForModeRoute(route.path);
    return requiredFeature ? { ...route, requiredFeature } : route;
  });

  const allRoutes = [
    ...modeRoutes,
    ...(showStudyList ? [WorkListRoute] : []),
    ...(customRoutes?.routes || []),
    ...bakedInRoutes,
    customRoutes?.notFoundRoute || notFoundRoute,
  ];

  function RouteWithErrorBoundary({ route, ...rest }) {
    // eslint-disable-next-line react/jsx-props-no-spreading
    const location = useLocation();

    useEffect(() => {
      if (location.pathname.startsWith('/viewer') || location.pathname === '/') {
        const metaViewport = document.querySelector('meta[name="viewport"]');
        if (metaViewport) {
          metaViewport.setAttribute('content', 'width=1024');
        }
      } else {
        const metaViewport = document.querySelector('meta[name="viewport"]');
        if (metaViewport) {
          metaViewport.setAttribute(
            'content',
            'width=device-width, initial-scale=1, minimum-scale=1, maximum-scale=1, user-scalable=0'
          );
        }
      }
    }, [location.pathname]);
    return (
      <ErrorBoundary context={`Route ${route.path}`}>
        <route.children
          {...rest}
          {...route.props}
          route={route}
          servicesManager={servicesManager}
          extensionManager={extensionManager}
          hotkeysManager={hotkeysManager}
        />
      </ErrorBoundary>
    );
  }

  const { userAuthenticationService } = servicesManager.services;

  // All routes are private by default and then we let the user auth service
  // to check if it is enabled or not
  // Todo: I think we can remove the second public return below
  return (
    <>
      <TokenRefreshManager />
      <ClinicFeatureSync />
      <Routes>
      {unauthorizedRoutes.map((route, i) => (
        <Route
          key={i}
          path={route.path}
          element={<RouteWithErrorBoundary route={route} />}
        />
      ))}
      {gpRoutes.map((route, i) => (
        <Route
          key={`gp-${i}`}
          path={route.path}
          element={<GpProtectedRoute element={<RouteWithErrorBoundary route={route} />} />}
        />
      ))}
      {mobileRoutes.map((route, i) => (
        <Route
          key={`mobile-${i}`}
          path={route.path}
          element={<RouteWithErrorBoundary route={route} />}
        />
      ))}
      {allRoutes.map((route, i) => {
        return route.private === true ? (
          <Route
            key={i}
            path={route.path}
            element={
              <PrivateRoute
                handleUnauthenticated={() => userAuthenticationService.handleUnauthenticated()}
              >
                <ProtectedRoute
                  element={<RouteWithErrorBoundary route={route} />}
                  allowedRoles={route.allowedRoles}
                  requiredFeature={route.requiredFeature}
                />
              </PrivateRoute>
            }
          ></Route>
        ) : (
          <Route
            key={i}
            path={route.path}
            element={
              <ProtectedRoute
                element={<RouteWithErrorBoundary route={route} />}
                allowedRoles={route.allowedRoles}
              />
            }
          />
        );
      })}
      </Routes>
    </>
  );
};

export default createRoutes;
